import { ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'next-i18next';
import IOSVideoPlayer from '@components/Video/IOSVideoPlayer';
import YouTubePlayer from '@components/Video/YouTubePlayer';
import { IParsedImage, IVideoType } from '@components/typescript';
import styles from '@components/Video/VideoPlayer.styles';
import getVideoUrl from '@utils/getVideoUrl';
import Loading from '@components/Loading/Loading';
import { useResponsive } from '@features/responsive';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
import useImageSources from '@hooks/useImageSources';
import getCurrentDevice from '@utils/getCurrentDevice';
import Icon from '@components/icons/Icon';
import theme from '@styles/theme';
import videoHasAudioTrack from '@utils/videoHasAudioTrack';
type PropsType = {
  video: IVideoType | null;
  playVideo: boolean;
  withSound?: boolean;
  isEndFunc?: () => void;
  poster?: string | IParsedImage;
  controls?: boolean;
  loop?: boolean;
  onLoaded?: () => void;
  fitVideo?: boolean;
  priority?: boolean;
  onErrorFunc?: () => void;
  fullscreen?: boolean;
  alt?: string;
  allowContextMenu?: boolean;
  onClick?: () => void;
  soundToggler?: boolean;
};

// Funkcja do wykrywania urządzeń iOS
const isIOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & {
    MSStream?: unknown;
  }).MSStream;
};

// Funkcja do sprawdzania czy to YouTube
const isYouTube = (videoUrl: string) => {
  if (videoUrl.startsWith('http')) {
    return videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
  }
  return false;
};
const isPlayInterruptedError = (error: unknown) => error instanceof DOMException && error.name === 'AbortError';
const VideoPlayer = forwardRef(({
  video,
  playVideo,
  withSound = false,
  isEndFunc = undefined,
  poster = undefined,
  controls = false,
  loop = false,
  onLoaded = undefined,
  fitVideo = false,
  priority = false,
  onErrorFunc,
  fullscreen = false,
  alt,
  allowContextMenu = false,
  onClick,
  soundToggler
}: PropsType, ref?: ForwardedRef<HTMLDivElement>) => {
  const {
    t
  } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(playVideo);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasNotifiedLoadedRef = useRef(false);
  const {
    currentBreakpoint
  } = useResponsive();
  const [isSoundOn, setIsSoundOn] = useState(withSound);
  const [hasAudioTrack, setHasAudioTrack] = useState(false);
  const isSoundOnRef = useRef(isSoundOn);
  useEffect(() => {
    setIsIOSDevice(isIOS());
  }, []);
  useEffect(() => {
    isSoundOnRef.current = isSoundOn;
  }, [isSoundOn]);
  useEffect(() => {
    setIsSoundOn(withSound);
  }, [withSound]);
  let imageSources: IParsedImage = {
    image: {
      mobile: {
        path: '',
        metadata: {
          alt: ''
        }
      },
      tablet: {
        path: '',
        metadata: {
          alt: ''
        }
      },
      desktop: {
        path: '',
        metadata: {
          alt: ''
        }
      }
    }
  };
  if (poster) {
    imageSources = typeof poster === 'string' ? {
      image: {
        mobile: {
          path: poster,
          metadata: {
            alt: ''
          }
        },
        tablet: {
          path: poster,
          metadata: {
            alt: ''
          }
        },
        desktop: {
          path: poster,
          metadata: {
            alt: ''
          }
        }
      }
    } : poster;
  }
  const {
    getSrcs
  } = useImageSources(imageSources);
  const {
    imageSrc: posterImg
  } = getSrcs(getCurrentDevice(currentBreakpoint));
  const currentVideoUrl = useMemo(() => {
    if (!video) {
      return undefined;
    }
    return getVideoUrl(video, currentBreakpoint) || undefined;
  }, [video, currentBreakpoint]);
  useEffect(() => {
    if (!video) {
      return;
    }
    if (!currentVideoUrl) {
      setIsLoading(false);
      onErrorFunc?.();
    }
  }, [video, currentVideoUrl, onErrorFunc]);
  const playVideoHandler = useCallback(async () => {
    if (isYouTube(currentVideoUrl || '')) {
      setIsPlaying(true);
      return;
    }
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }
    const wantsSound = isSoundOnRef.current;
    videoElement.muted = !wantsSound;
    try {
      await videoElement.play();
      setIsPlaying(true);
    } catch (error) {
      if (isPlayInterruptedError(error)) {
        return;
      }
      if (wantsSound) {
        videoElement.muted = true;
        try {
          await videoElement.play();
          setIsPlaying(true);
          setIsSoundOn(false);
        } catch (retryError) {
          if (isPlayInterruptedError(retryError)) {
            return;
          }
          if (process.env.NODE_ENV === 'development') {
            console.error('Error playing video:', retryError);
          }
        }
        return;
      }
      if (process.env.NODE_ENV === 'development') {
        console.error('Error playing video:', error);
      }
    }
  }, [currentVideoUrl]);
  const pauseVideo = useCallback(() => {
    if (isYouTube(currentVideoUrl || '')) {
      setIsPlaying(false);
    } else if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [currentVideoUrl]);
  const checkAudioTrack = useCallback(() => {
    if (videoRef.current && videoHasAudioTrack(videoRef.current)) {
      setHasAudioTrack(true);
    }
  }, []);
  const enableSound = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement?.muted) {
      return false;
    }
    videoElement.muted = false;
    setIsSoundOn(true);
    return true;
  }, []);
  const handleVideoReady = useCallback(() => {
    if (hasNotifiedLoadedRef.current) {
      return;
    }
    hasNotifiedLoadedRef.current = true;
    setIsLoading(false);
    if (onLoaded) onLoaded();
    if (playVideo) {
      playVideoHandler();
    }
  }, [onLoaded, playVideo, playVideoHandler]);
  const syncVideoReadyFromElement = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement || videoElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return;
    }
    handleVideoReady();
    checkAudioTrack();
  }, [handleVideoReady, checkAudioTrack]);
  const handleIframeLoad = () => {
    setIsLoading(false);
    if (onLoaded) onLoaded();
    if (playVideo) {
      setIsPlaying(true);
    }
  };
  const handleEndVideo = () => {
    setIsPlaying(false);
    if (isEndFunc) isEndFunc();
  };
  const handleError = () => {
    setIsLoading(false);
    if (onErrorFunc) onErrorFunc();
  };
  useEffect(() => {
    if (playVideo) {
      playVideoHandler();
    } else {
      pauseVideo();
    }
  }, [playVideo, playVideoHandler, pauseVideo]);
  useEffect(() => {
    hasNotifiedLoadedRef.current = false;
    setHasAudioTrack(false);
    setIsLoading(true);
  }, [currentVideoUrl]);
  useEffect(() => {
    if (!currentVideoUrl || isYouTube(currentVideoUrl)) {
      return;
    }
    syncVideoReadyFromElement();
  }, [currentVideoUrl, syncVideoReadyFromElement]);
  const posterAlt = alt || t('productVideo');
  const Poster = poster ? typeof poster === 'string' ? <ResponsiveImage image={poster} alt={posterAlt} sx={styles.thumbnail} priority={priority} /> : <ResponsiveImage image={poster.image} blur={poster.blur} alt={posterAlt} sx={styles.thumbnail} priority={priority} /> : null;

  // Renderowanie dla iOS (only after client-side detection to avoid hydration mismatch)
  if (isIOSDevice) {
    return <div sx={styles.videoContainer} ref={ref}>
          <IOSVideoPlayer video={currentVideoUrl || ''} loop={loop} poster={typeof posterImg === 'string' ? posterImg : posterImg?.path || ''} play={playVideo} onLoaded={onLoaded} onEnded={handleEndVideo} onError={handleError} withSound={withSound} controls={controls} fitVideo={fitVideo} fullscreen={fullscreen} alt={alt} onClick={onClick} soundToggler={soundToggler} />
        </div>;
  }

  // Renderowanie dla YouTube
  if (currentVideoUrl && isYouTube(currentVideoUrl)) {
    return <div sx={styles.videoContainer} ref={ref}>
          <YouTubePlayer video={currentVideoUrl} isPlaying={isPlaying} isLoading={isLoading} loop={loop} onPlay={playVideoHandler} onLoad={handleIframeLoad} onEnded={handleEndVideo} controls={controls} withSound={withSound} title={alt} />
        </div>;
  }
  const handleSoundToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsSoundOn(prev => {
      const next = !prev;
      if (videoRef.current) {
        videoRef.current.muted = !next;
      }
      return next;
    });
  };
  const handleVideoClick = () => {
    if (withSound && enableSound()) {
      return;
    }
    onClick?.();
  };

  // Renderowanie standardowego video
  return <div sx={styles.videoContainer} ref={ref}>
        {isLoading && (poster ? Poster : <Loading />)}
        {soundToggler && hasAudioTrack && <button type="button" onClick={handleSoundToggle} sx={styles.soundToggler} aria-label={t(isSoundOn ? 'accessibility.turnSoundOff' : 'accessibility.turnSoundOn')}>
            {!isSoundOn ? <Icon icon="SpeakerOff" color={theme.rawColors.neutralWhite} /> : <Icon icon="SpeakerOn" color={theme.rawColors.neutralWhite} />}
          </button>}
        <video ref={videoRef} src={currentVideoUrl} muted={!isSoundOn} loop={loop} playsInline preload="metadata" webkit-playsinline="true" x5-playsinline="true" x-webkit-airplay="allow" controls={controls} disablePictureInPicture={!fullscreen} tabIndex={controls ? 0 : -1} sx={styles.videoStyles(fullscreen, fitVideo)} onLoadedData={syncVideoReadyFromElement} onCanPlay={syncVideoReadyFromElement} onPlaying={checkAudioTrack} onEnded={handleEndVideo} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onError={handleError} onContextMenu={e => !allowContextMenu && e.preventDefault()} aria-label={alt || t('productVideo')} onClick={handleVideoClick} />
      </div>;
});
VideoPlayer.displayName = 'VideoPlayer';
export default VideoPlayer;